/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import flow from 'lodash.flow';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { fileService } from 'src/core/services/api/fileService';
import { getAzureBlobService } from 'src/core/services/azure/azureBlobService';
import { getAzureContainerService } from 'src/core/services/azure/azureContainerService';
import { AzureBlobStore } from 'src/core/stores/AzureBlobStore';
import { OperationDelayStrategies } from 'src/core/utils/api/operationDelayStrategies';
import { getStorageUrl } from 'src/core/utils/api/storageUtils';
import { addToast } from 'src/core/utils/decorators/addToast';
import { addTracker } from 'src/core/utils/decorators/addTracker';
import { getRandomNumber } from 'src/core/utils/randomValueGenerators';
import { commonDemosMessages, resourceEndpointCurlPlaceholder } from 'src/languageStudio/appShell/prebuiltDemos/utils/commonDemoMessages';
import { getTranslationService } from 'src/languageStudio/core/services/translation/translationService';
import {
    InputDocument,
    TextTranslationSwagger,
    TranslationDocumentSwagger,
    TranslationLanguage
} from 'src/languageStudio/core/services/translation/translatorSwagger';
import { AzureResourcesStrategies } from 'src/modules/azure/utils/resourcesStrategies';

export type TranslatorLanguage = TranslationLanguage & { code: string };
export type TargetFile = (Partial<AzureBlobStore> | File) & { containerName: string; languageCode?: string };
export type DocumentTranslationStatus = 'documentPreparing' | 'translatingStarts' | 'TranslatingInprogress';

const messages = defineMessages({
    uploadFilesInprogress: { id: 'languageStudio.translationDemoPage.uploadFilesInprogress', defaultMessage: 'Uploading local files...' },
    uploadFilesCompleted: {
        id: 'languageStudio.translationDemoPage.uploadFilesCompleted',
        defaultMessage: 'Uploading local files successfully completed'
    },
    azureResourceErrorMessage: {
        id: 'languageStudio.translationDemoPage.azureResourceErrorMessage',
        defaultMessage: 'The selected azure resource is invalid.'
    }
});

const getGlossaryFormat = (glossaryExtension: string) => {
    switch (glossaryExtension) {
        case 'xlf':
            return 'xliff';
        default:
            return glossaryExtension;
    }
};

export const useTranslatorCapabilities = () => {
    const tracker = useProgressTracker();
    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();

    const azureBlobService = getAzureBlobService();
    const translatorService = getTranslationService();
    const azureResource = rootStore.azureStore.session.csAccountStore;

    const [supportedLanguages, setSupportedLanguages] = React.useState<TranslatorLanguage[]>([]);
    const [textTranslationResult, setTextTranslationResult] = React.useState<TextTranslationSwagger>(null);
    const [documentsTranslationStatus, setDocumentsTranslationStatus] = React.useState<DocumentTranslationStatus>(null);
    const [documentsTranslationResult, setDocumentsTranslationResult] = React.useState<TranslationDocumentSwagger[]>(null);

    const resetResults = () => {
        tracker.reset();
        setTextTranslationResult(null);
        setDocumentsTranslationResult(null);
    };

    const validate = (language: boolean, input: boolean) => {
        let error: Error;
        const isInvalidInput = !input;
        const isInvalidLanguage = !language;
        const isInvalidResource = !AzureResourcesStrategies.translatorResourceStrategy(azureResource);

        if (isInvalidResource) {
            error = new Error(localize(messages.azureResourceErrorMessage));
            error.name = localize(commonDemosMessages.azureResourceErrorTitle);
        } else if (isInvalidLanguage) {
            error = new Error(localize(commonDemosMessages.languageErrorMessage));
            error.name = localize(commonDemosMessages.languageErrorTitle);
        } else if (isInvalidInput) {
            error = new Error(localize(commonDemosMessages.inputErrorMessage));
            error.name = localize(commonDemosMessages.inputErrorTitle);
        }

        if (isInvalidInput || isInvalidLanguage || isInvalidResource) {
            const inputStep = document.querySelector('#demoPageDocumentInputStep');
            inputStep?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

            throw error;
        }
    };

    const addDocNameToTarget = (document: InputDocument) => ({
        ...document,
        targets: document.targets.map(t => ({
            ...t,
            targetUrl: `${t.targetUrl}/${t.language}-${document.name}`
        }))
    });

    const getCurlCommand = ({
        text,
        endpoint,
        headersToAdd = ''
    }: {
        text: string;
        endpoint: string;
        headersToAdd?: string;
    }): string => `@ECHO OFF

curl -X POST "${azureResource?.endpoint ??
        resourceEndpointCurlPlaceholder}${endpoint}" -H "Content-Type: application/json; charset=UTF-8" -H "Ocp-Apim-Subscription-Key: ${azureResource?.serviceKey ??
        localize(commonDemosMessages.subscriptionKeyCurlPlaceholder)}" ${headersToAdd} -d "${JSON.stringify([{ Text: text }])
        .split('"')
        .join('\\"')}" `;

    const getSupportedLanguages = () =>
        flow(
            async () => {
                const translationLanguagesObj = await translatorService.getSupportedLanguages();
                const languagesList = Object.keys(translationLanguagesObj.translation).map(languageCode => ({
                    code: languageCode,
                    ...translationLanguagesObj.translation[languageCode]
                }));
                setSupportedLanguages(languagesList);
            },
            addToast({ toastData: {} }),
            addTracker({ tracker })
        )();

    const translateText = ({
        text,
        sourceLanguage,
        targetLanguage,
        deleteProfanity,
        transliteration
    }: {
        text: string;
        sourceLanguage: string;
        targetLanguage: string;
        deleteProfanity?: boolean;
        transliteration?: boolean;
    }) =>
        flow(
            resetResults,
            async () => {
                validate(Boolean(targetLanguage), Boolean(text));

                setTextTranslationResult(
                    await translatorService.translateText({
                        text,
                        targetLanguage,
                        sourceLanguage,
                        transliteration,
                        deleteProfanity,
                        resourceLocation: azureResource.region
                    })
                );
            },
            addToast({ toastData: {} }),
            addTracker({ tracker })
        )();

    const translateDocuments = ({
        files,
        isAllFiles,
        glossaryFiles,
        targetLanguages,
        resourceGroupName,
        storageAccountName,
        downloadTranslatedFiles,
        destinationContainerName
    }: {
        isAllFiles: boolean;
        files: TargetFile[];
        resourceGroupName: string;
        targetLanguages: string[];
        storageAccountName: string;
        glossaryFiles?: TargetFile[];
        downloadTranslatedFiles: boolean;
        destinationContainerName: string;
    }) =>
        flow(
            resetResults,
            async () => {
                setDocumentsTranslationStatus('documentPreparing');

                // Check the selected target container if there is none with
                // download option active then create one on the fly
                const targetContainerName = destinationContainerName ?? `language-studio-targets-${getRandomNumber()}`;

                if (!destinationContainerName && downloadTranslatedFiles) {
                    await getAzureContainerService().createAzureContainer({
                        resourceGroupName,
                        storageAccountName,
                        containerName: targetContainerName,
                        token: rootStore.azureStore.session.userTokens.armToken,
                        subscriptionId: azureResource.subscriptionId
                    });
                }

                // Validates the inputs
                validate(Boolean(targetLanguages.length), Boolean(files.length) && Boolean(targetContainerName));

                // Start preparing the documents.
                const targets: InputDocument['targets'] = targetLanguages.map(t => ({
                    language: t,
                    targetUrl: getStorageUrl({ storageAccountName, containerName: targetContainerName }),
                    glossaries: glossaryFiles?.map(g => {
                        const glossaryFileNameComponents = g.name.split('.');

                        return {
                            format: getGlossaryFormat(glossaryFileNameComponents[glossaryFileNameComponents.length - 1]),
                            glossaryUrl: getStorageUrl({ storageAccountName, containerName: g.containerName, blobName: g.name })
                        };
                    })
                }));
                const documents: InputDocument[] = isAllFiles
                    ? [
                          {
                              targets,
                              name: files[0].containerName,
                              source: { sourceUrl: getStorageUrl({ storageAccountName, containerName: files[0].containerName }) }
                          }
                      ]
                    : files.map<InputDocument>(f => ({
                          targets,
                          name: f.name,
                          storageType: 'File',
                          source: {
                              sourceUrl: getStorageUrl({ storageAccountName, containerName: f.containerName, blobName: f.name })
                          }
                      }));

                // Initiate translation operation.
                setDocumentsTranslationStatus('translatingStarts');
                let filesToDownload: TranslationDocumentSwagger[];

                await translatorService.translateDocumentsAndFetchDocs({
                    documents: documents.map(addDocNameToTarget),
                    handler: {
                        timeToDelay: OperationDelayStrategies.exponentialDelayStrategy,
                        handleCall: res => {
                            filesToDownload = res.value;
                            setDocumentsTranslationStatus('TranslatingInprogress');
                            setDocumentsTranslationResult(res.value);
                        },
                        retryStrategy: res =>
                            res.data.value.reduce(
                                (acc, document) => acc || document.status === 'Running' || document.status === 'NotStarted',
                                false
                            )
                    },
                    resourceName: azureResource.name
                });

                // Download files if needed.
                if (downloadTranslatedFiles) {
                    const documentContents = await Promise.all(
                        filesToDownload.map(d =>
                            azureBlobService.getAzureBlobContent({
                                blobName: '',
                                blobUri: d.path,
                                storageAccountName,
                                containerName: targetContainerName,
                                storageToken: rootStore.azureStore.session.userTokens.storageToken
                            })
                        )
                    );

                    filesToDownload.map((d, dIndex) => {
                        const sourceUrlTokens = d.sourcePath.split('/');
                        const fileName = sourceUrlTokens[sourceUrlTokens.length - 1];

                        fileService.saveToDisk(documentContents[dIndex], fileName);
                    });
                }
            },
            addToast({ toastData: {} }),
            addTracker({ tracker })
        )();

    const uploadLocalFiles = ({
        files,
        onComplete,
        folderName,
        containerName,
        resourceGroupName,
        storageAccountName
    }: {
        files: File[];
        folderName?: string;
        containerName: string;
        resourceGroupName: string;
        storageAccountName: string;
        onComplete?: (containerName: string) => void;
    }) =>
        flow(
            resetResults,
            async () => {
                await getAzureContainerService().createAzureContainer({
                    containerName,
                    resourceGroupName,
                    storageAccountName,
                    token: rootStore.azureStore.session.userTokens.armToken,
                    subscriptionId: azureResource.subscriptionId
                });

                const blobsToUpload = files.map(blob =>
                    fileService.readFromDiskAsArrayBuffer(blob).then(blobContent =>
                        azureBlobService.uploadAzureBlobByContainerName({
                            blobContent,
                            containerName,
                            storageAccountName,
                            storageToken: rootStore.azureStore.session.userTokens.storageToken,
                            blobName: `${folderName ? `${folderName}/` : ''}${blob.name}`
                        })
                    )
                );

                await Promise.all(blobsToUpload);

                onComplete?.(containerName);
            },
            addToast({
                toastData: {
                    running: { title: localize(messages.uploadFilesInprogress) },
                    completed: { title: localize(messages.uploadFilesCompleted) }
                }
            }),
            addTracker({ tracker })
        )();

    return {
        tracker,
        resetResults,
        translateText,
        getCurlCommand,
        uploadLocalFiles,
        supportedLanguages,
        translateDocuments,
        textTranslationResult,
        getSupportedLanguages,
        documentsTranslationStatus,
        documentsTranslationResult
    };
};
